import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Player from './Player';
import { SiteHeader, SiteFooter, StateCard, WatchSkeleton } from './SiteChrome';
import {
  IconEye, IconClock, IconList, IconLink, IconLock, IconCheck, IconKeyboard, IconUser, IconMail,
  IconShield, IconPlay, IconChevronRight,
} from './Icons';
import { BRAND_NAME } from './brand';
import {
  storage, sessionStore, progressKey, doneKey, formatTime, formatDuration, formatCount, pluralRu,
  parseTimeParam, copyText,
} from './utils';
import './VideoPlayer.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const VIEWER_KEY = 'vp:viewer';
const THEATER_KEY = 'vp:theater';

function useSessionId() {
  const ref = useRef(null);
  if (!ref.current) ref.current = 'sess_' + Math.random().toString(36).slice(2) + Date.now();
  return ref.current;
}

function WatermarkOverlay({ fullName, email, clientIp, moveIntervalSeconds = 15, showIp = true, opacity = 0.6, fontSize = 12 }) {
  const [position, setPosition] = useState({ x: 12, y: 12 });

  useEffect(() => {
    if (!fullName && !email) return undefined;
    const intervalMs = Math.max(5000, Math.min(120000, (moveIntervalSeconds || 15) * 1000));
    const interval = setInterval(() => {
      setPosition({
        x: Math.floor(Math.random() * 75) + 5,
        y: Math.floor(Math.random() * 75) + 5,
      });
    }, intervalMs);
    return () => clearInterval(interval);
  }, [fullName, email, moveIntervalSeconds]);

  const parts = [fullName, email];
  if (showIp && clientIp) parts.push(clientIp);
  const text = parts.filter(Boolean).join(' · ');

  return (
    <div className="watermark-overlay" style={{ left: `${position.x}%`, top: `${position.y}%` }} aria-hidden="true">
      <span
        className="watermark-text"
        style={{
          opacity: Math.max(0.2, Math.min(1, opacity)),
          fontSize: `${Math.max(10, Math.min(24, fontSize))}px`,
        }}
      >
        {text}
      </span>
      <span className="watermark-text watermark-text-duplicate" aria-hidden="true" style={{ opacity: 0, fontSize: `${fontSize}px` }}>
        {text}
      </span>
    </div>
  );
}

// Описание: ссылки и таймкоды (12:34 / 1:02:03) становятся кликабельными
const DESC_TOKEN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]])|(\b\d{1,2}:\d{2}(?::\d{2})?\b)/g;

function RichDescription({ text, onSeek, duration }) {
  const nodes = [];
  let last = 0;
  let m;
  DESC_TOKEN.lastIndex = 0;
  while ((m = DESC_TOKEN.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) {
      nodes.push(
        <a key={m.index} href={m[1]} target="_blank" rel="noopener noreferrer">
          {m[1].replace(/^https?:\/\//, '')}
        </a>
      );
    } else if (duration && parseTimeParam(m[2]) < duration) {
      const sec = parseTimeParam(m[2]);
      nodes.push(
        <button key={m.index} type="button" className="timecode" onClick={() => onSeek(sec)}>
          {m[2]}
        </button>
      );
    } else {
      nodes.push(m[2]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function PlaylistPanel({ playlist, currentId, onSelect }) {
  const listRef = useRef(null);
  const videos = playlist.videos || [];
  const currentIndex = videos.findIndex((v) => v.id === currentId);
  const doneCount = videos.filter((v) => storage.get(doneKey(v.id))).length;

  useEffect(() => {
    const list = listRef.current;
    const active = list && list.querySelector('.pl-item.active');
    if (list && active) list.scrollTop = active.offsetTop - list.offsetTop - 8;
  }, [currentId]);

  return (
    <div className="card playlist-panel">
      <div className="playlist-panel-head">
        <div className="playlist-panel-icon">
          <IconList size={18} />
        </div>
        <div className="playlist-panel-titles">
          <div className="playlist-panel-label">Плейлист</div>
          <div className="playlist-panel-title" title={playlist.title}>{playlist.title}</div>
        </div>
        <div className="playlist-panel-count">
          {currentIndex >= 0 ? currentIndex + 1 : '–'} / {videos.length}
        </div>
      </div>
      <div className="playlist-panel-progress" title={`Просмотрено ${doneCount} из ${videos.length}`}>
        <div style={{ width: `${videos.length ? (doneCount / videos.length) * 100 : 0}%` }} />
      </div>
      <ol ref={listRef} className="pl-list">
        {videos.map((v, i) => {
          const active = v.id === currentId;
          const done = !!storage.get(doneKey(v.id));
          return (
            <li key={v.id}>
              <button
                type="button"
                className={`pl-item ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                onClick={() => !active && onSelect(v.id)}
                aria-current={active ? 'true' : undefined}
              >
                <span className="pl-index">
                  {active ? (
                    <span className="eq" aria-label="Сейчас играет"><i /><i /><i /></span>
                  ) : done ? (
                    <IconCheck size={14} />
                  ) : (
                    i + 1
                  )}
                </span>
                <span className="pl-title">{v.title}</span>
                {v.duration != null && <span className="pl-dur">{formatTime(v.duration)}</span>}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function AccessGate({ video, form, setForm, error, submitting, onSubmit }) {
  return (
    <div className="access-wrap">
      <div className="access-card">
        <div className="access-aside">
          <div className="access-aside-icon">
            <IconLock size={26} />
          </div>
          <div className="access-eyebrow">Закрытый просмотр</div>
          <h1 className="access-video-title">{video.playlist_title || video.title}</h1>
          <ul className="access-points">
            <li><IconShield size={16} /> Доступ только для приглашённых зрителей</li>
            <li><IconCheck size={16} /> Проверка занимает пару секунд</li>
            <li><IconPlay size={14} /> Прогресс просмотра сохраняется</li>
          </ul>
        </div>
        <form className="access-form" onSubmit={onSubmit}>
          <h2 className="access-title">Подтвердите доступ</h2>
          <p className="access-subtitle">Укажите ФИО и email, на которые вам открыли доступ.</p>
          <label className="field">
            <span className="field-label">ФИО</span>
            <span className="field-control">
              <IconUser size={18} />
              <input
                type="text"
                autoComplete="name"
                placeholder="Иванов Иван Иванович"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                required
              />
            </span>
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <span className="field-control">
              <IconMail size={18} />
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="email@example.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            </span>
          </label>
          {error && <p className="access-error" role="alert">{error}</p>}
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={submitting}>
            {submitting ? <span className="btn-spinner" /> : null}
            {submitting ? 'Проверяем…' : 'Перейти к просмотру'}
            {!submitting && <IconChevronRight size={18} />}
          </button>
          <p className="access-note">Данные используются только для проверки доступа и защиты материалов.</p>
        </form>
      </div>
    </div>
  );
}

function VideoPlayer() {
  const { videoId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const playlistId = searchParams.get('playlist_id') || null;
  const embed = searchParams.get('embed') === '1' || searchParams.get('embed') === 'true';
  const tParam = searchParams.get('t');
  const location = useLocation();
  // «Только плеер»: код встраивания /embed/{id} — без шапки, описания и плейлиста
  const chromeless = searchParams.get('player') === '1' || location.pathname.startsWith('/embed/');

  // Страница загружена в iframe (а не открыта напрямую)
  const isInIframe = window.self !== window.top;
  const embedFlag = embed || isInIframe;

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [accessForm, setAccessForm] = useState(() => sessionStore.get(VIEWER_KEY, null) || { full_name: '', email: '' });
  const [accessError, setAccessError] = useState(null);
  const [accessSubmitting, setAccessSubmitting] = useState(false);
  const [autoChecking, setAutoChecking] = useState(false);
  const [hlsToken, setHlsToken] = useState(null);
  const [viewer, setViewer] = useState({ name: null, email: null, ip: null });
  const [watermarkSettings, setWatermarkSettings] = useState({
    move_interval_seconds: 15,
    show_ip: true,
    opacity: 0.6,
    font_size: 12,
  });
  const [theater, setTheater] = useState(() => !!storage.get(THEATER_KEY, false));
  const [descExpanded, setDescExpanded] = useState(false);
  const [copied, setCopied] = useState(null);

  const videoRef = useRef(null);
  const watchStartedRef = useRef(null);
  const sessionId = useSessionId();

  const buildWatchUrl = useCallback(
    (id, { forShare = false } = {}) => {
      const params = new URLSearchParams();
      if (playlistId) params.set('playlist_id', playlistId);
      if (!forShare && embedFlag) params.set('embed', '1');
      if (!forShare && chromeless) params.set('player', '1');
      const qs = params.toString();
      return `/watch/${id}${qs ? `?${qs}` : ''}`;
    },
    [playlistId, embedFlag, chromeless]
  );

  // Смена видео: токен привязан к конкретному видео — сбрасываем состояние
  useEffect(() => {
    setLoading(true);
    setError(null);
    setVideo(null);
    setHlsToken(null);
    setAccessError(null);
    setDescExpanded(false);
    const params = {};
    if (playlistId) params.playlist_id = playlistId;
    if (embed) params.embed = '1';
    axios
      .get(`${API_URL}/api/public/videos/${videoId}`, { params })
      .then((res) => setVideo(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Видео не найдено'))
      .finally(() => setLoading(false));
  }, [videoId, playlistId, embed]);

  useEffect(() => {
    if (!playlistId) {
      setPlaylist(null);
      return;
    }
    axios
      .get(`${API_URL}/api/public/playlists/${playlistId}`)
      .then((r) => setPlaylist(r.data))
      .catch(() => setPlaylist(null));
  }, [playlistId]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/public/settings/watermark`)
      .then((r) => setWatermarkSettings(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.title = video ? `${video.title} — ${BRAND_NAME}` : BRAND_NAME;
  }, [video]);

  useEffect(() => {
    storage.set(THEATER_KEY, theater);
  }, [theater]);

  // Публичное видео: получаем токен сразу
  useEffect(() => {
    if (!video || video.status !== 'ready' || video.access_by_list || hlsToken) return;
    const params = {};
    if (embedFlag) params.embed = '1';
    if (playlistId) params.playlist_id = playlistId;
    axios
      .get(`${API_URL}/api/public/videos/${videoId}/hls-token`, { params })
      .then((r) => setHlsToken(r.data.token))
      .catch(() => setError('Не удалось получить доступ к видео'));
  }, [video, videoId, playlistId, hlsToken, embedFlag]);

  const runAccessCheck = useCallback(
    (form) => {
      const params = {};
      if (playlistId) params.playlist_id = playlistId;
      if (embedFlag) params.embed = '1';
      return axios
        .post(`${API_URL}/api/public/videos/${videoId}/access-check`, form, { params })
        .then((res) => {
          if (!res.data.allowed) {
            setAccessError(res.data.message || 'Доступ запрещён');
            return false;
          }
          const clean = { full_name: form.full_name.trim(), email: form.email.trim() };
          sessionStore.set(VIEWER_KEY, clean);
          setViewer({ name: clean.full_name, email: clean.email, ip: res.data.client_ip || '' });
          setHlsToken(res.data.token);
          return true;
        })
        .catch((err) => {
          setAccessError(err.response?.data?.detail || 'Ошибка проверки доступа');
          return false;
        });
    },
    [videoId, playlistId, embedFlag]
  );

  // Доступ по списку: если зритель уже вводил данные в этой вкладке — проверяем автоматически
  useEffect(() => {
    if (!video || !video.access_by_list || hlsToken) return;
    const remembered = sessionStore.get(VIEWER_KEY, null);
    if (!remembered || !remembered.email) return;
    setAutoChecking(true);
    runAccessCheck(remembered).finally(() => setAutoChecking(false));
  }, [video, hlsToken, runAccessCheck]);

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    setAccessError(null);
    setAccessSubmitting(true);
    runAccessCheck(accessForm).finally(() => setAccessSubmitting(false));
  };

  const hlsUrl = hlsToken && video ? `${API_URL}/api/hls/stream/${videoId}/master.m3u8?token=${encodeURIComponent(hlsToken)}` : null;
  const hlsUrlFallback = hlsToken && video ? `${API_URL}/api/hls/stream/${videoId}/playlist.m3u8?token=${encodeURIComponent(hlsToken)}` : null;

  // Стартовая позиция: ?t= из ссылки, иначе сохранённый прогресс
  const { startAt, resumedFrom } = useMemo(() => {
    const t = parseTimeParam(tParam);
    if (t) return { startAt: t, resumedFrom: 0 };
    const saved = storage.get(progressKey(videoId), 0);
    return saved > 0 ? { startAt: saved, resumedFrom: saved } : { startAt: 0, resumedFrom: 0 };
  }, [videoId, tParam]);

  // ---------- Статистика просмотра ----------
  useEffect(() => {
    if (!video || video.status !== 'ready' || !hlsToken || !videoRef.current) return undefined;

    const el = videoRef.current;
    watchStartedRef.current = new Date().toISOString();

    const sendTrack = (watchTime, percent, ended) => {
      const params = { session_id: sessionId };
      if (viewer.name) params.viewer_name = viewer.name;
      if (viewer.email) params.viewer_email = viewer.email;
      if (playlistId) params.playlist_id = playlistId;
      axios
        .post(
          `${API_URL}/api/public/videos/${videoId}/track`,
          {
            watch_time: watchTime,
            percent_watched: percent,
            watch_started_at: watchStartedRef.current,
            watch_ended_at: ended ? new Date().toISOString() : undefined,
          },
          { params }
        )
        .catch(() => {});
    };

    let lastTrackedSecond = -1;
    const interval = setInterval(() => {
      if (!el.duration || el.paused) return;
      const current = Math.floor(el.currentTime);
      if (current > 0 && current % 10 === 0 && current !== lastTrackedSecond) {
        lastTrackedSecond = current;
        sendTrack(current, Math.round((el.currentTime / el.duration) * 100), false);
      }
    }, 1000);

    const onEnded = () => {
      if (el.duration) sendTrack(Math.floor(el.duration), 100, true);
    };
    el.addEventListener('ended', onEnded);

    return () => {
      clearInterval(interval);
      el.removeEventListener('ended', onEnded);
      if (el.duration && el.currentTime > 0) {
        sendTrack(Math.floor(el.currentTime), Math.round((el.currentTime / el.duration) * 100), true);
      }
    };
  }, [video, videoId, hlsToken, viewer.name, viewer.email, playlistId, sessionId]);

  // ---------- Действия ----------
  const nextInfo = useMemo(() => {
    if (!video || !video.next_video_id) return null;
    const fromList = playlist && (playlist.videos || []).find((v) => v.id === video.next_video_id);
    return { id: video.next_video_id, title: fromList ? fromList.title : null };
  }, [video, playlist]);

  const goToVideo = useCallback((id) => navigate(buildWatchUrl(id)), [navigate, buildWatchUrl]);
  const goNext = useCallback(() => nextInfo && goToVideo(nextInfo.id), [nextInfo, goToVideo]);

  const seekTo = (sec) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = sec;
    el.play().catch(() => {});
    const wrap = document.querySelector('.watch-player-area');
    if (wrap && wrap.getBoundingClientRect().top < 0) wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const share = async (withTime) => {
    let url = window.location.origin + buildWatchUrl(videoId, { forShare: true });
    if (withTime && videoRef.current) {
      const t = Math.floor(videoRef.current.currentTime);
      url += `${url.includes('?') ? '&' : '?'}t=${t}`;
    }
    const ok = await copyText(url);
    setCopied(ok ? (withTime ? 'time' : 'link') : 'error');
    setTimeout(() => setCopied(null), 2200);
  };

  const pageClass = `watch-page ${isInIframe ? 'is-embedded' : ''} ${chromeless ? 'is-chromeless' : ''}`;
  const header = !isInIframe && !chromeless && (
    <SiteHeader>
      {playlist && (
        <span className="header-pill">
          <IconList size={15} /> {playlist.title}
        </span>
      )}
    </SiteHeader>
  );

  // ---------- Состояния ----------
  if (loading) {
    return (
      <div className={pageClass}>
        {header}
        <main className="watch-main">
          <WatchSkeleton withSidebar={!!playlistId} />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageClass}>
        {header}
        <main className="watch-main watch-center">
          <StateCard
            tone="danger"
            title="Не удалось открыть видео"
            text={typeof error === 'string' ? error : 'Попробуйте обновить страницу'}
            action={
              <button type="button" className="btn btn-secondary" onClick={() => window.location.reload()}>
                Обновить страницу
              </button>
            }
          />
        </main>
      </div>
    );
  }

  // Блокируем прямой доступ только для видео с флагом embed_only
  if (video && video.embed_only && (!embed || !isInIframe)) {
    return (
      <div className={pageClass}>
        {header}
        <main className="watch-main watch-center">
          <StateCard
            icon={<IconLock size={26} />}
            title="Доступ запрещён"
            text="Видео доступно только через код встраивания на разрешённых сайтах."
          />
        </main>
      </div>
    );
  }

  if (!video || video.status !== 'ready') {
    return (
      <div className={pageClass}>
        {header}
        <main className="watch-main watch-center">
          <StateCard
            icon={<IconClock size={26} />}
            tone="attention"
            title="Видео ещё обрабатывается"
            text="Обычно это занимает несколько минут. Обновите страницу чуть позже."
          />
        </main>
      </div>
    );
  }

  if (video.access_by_list && !hlsToken) {
    return (
      <div className={pageClass}>
        {header}
        <main className="watch-main watch-center">
          {autoChecking ? (
            <StateCard icon={<span className="btn-spinner btn-spinner-accent" />} title="Проверяем доступ…" />
          ) : (
            <AccessGate
              video={video}
              form={accessForm}
              setForm={setAccessForm}
              error={accessError}
              submitting={accessSubmitting}
              onSubmit={handleAccessSubmit}
            />
          )}
        </main>
      </div>
    );
  }

  const showWatermark = video.access_by_list && hlsToken && (viewer.name || viewer.email);
  const hasSidebar = !!(playlist && playlist.videos && playlist.videos.length);
  const canShare = !video.embed_only && !isInIframe;
  const longDesc = video.description && (video.description.length > 260 || video.description.split('\n').length > 4);

  return (
    <div className={pageClass}>
      {header}
      <main className={`watch-main ${theater ? 'is-theater' : ''}`}>
        <div className={`watch-layout ${hasSidebar ? 'has-sidebar' : ''} ${theater ? 'is-theater' : ''}`}>
          <div className="watch-player-area">
            {hlsUrl ? (
              <Player
                key={videoId}
                videoRef={videoRef}
                videoId={videoId}
                title={video.title}
                showTitle={chromeless}
                src={hlsUrl}
                fallbackSrc={hlsUrlFallback}
                startAt={startAt}
                resumedFrom={resumedFrom}
                next={nextInfo}
                onNext={goNext}
                theater={theater}
                onToggleTheater={isInIframe ? undefined : () => setTheater((t) => !t)}
                onFatalError={setError}
                overlay={
                  showWatermark ? (
                    <WatermarkOverlay
                      fullName={viewer.name}
                      email={viewer.email}
                      clientIp={viewer.ip}
                      moveIntervalSeconds={watermarkSettings.move_interval_seconds}
                      showIp={watermarkSettings.show_ip}
                      opacity={watermarkSettings.opacity}
                      fontSize={watermarkSettings.font_size}
                    />
                  ) : null
                }
              />
            ) : (
              <div className="skeleton skeleton-player" />
            )}
          </div>

          {!chromeless && (
            <div className="watch-info-area">
              <section className="card watch-info">
                {video.playlist_title && (
                  <div className="watch-eyebrow">
                    <IconList size={14} /> {video.playlist_title}
                    {playlist && playlist.videos && (
                      <span className="watch-eyebrow-count">
                        · видео {Math.max(1, playlist.videos.findIndex((v) => v.id === videoId) + 1)} из {playlist.videos.length}
                      </span>
                    )}
                  </div>
                )}
                <h1 className="watch-title">{video.title}</h1>

                <div className="watch-meta">
                  <span className="meta-chip">
                    <IconEye size={16} />
                    {formatCount(video.views)} {pluralRu(video.views || 0, 'просмотр', 'просмотра', 'просмотров')}
                  </span>
                  {video.duration ? (
                    <span className="meta-chip">
                      <IconClock size={16} />
                      {formatDuration(video.duration)}
                    </span>
                  ) : null}
                  {video.access_by_list && (
                    <span className="meta-chip meta-chip-success">
                      <IconShield size={16} /> Закрытый доступ
                    </span>
                  )}

                  <div className="watch-actions">
                    {canShare && (
                      <>
                        <button type="button" className="btn btn-ghost" onClick={() => share(false)}>
                          {copied === 'link' ? <IconCheck size={16} /> : <IconLink size={16} />}
                          {copied === 'link' ? 'Скопировано' : 'Ссылка'}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => share(true)} title="Скопировать ссылку с текущего момента">
                          {copied === 'time' ? <IconCheck size={16} /> : <IconClock size={16} />}
                          {copied === 'time' ? 'Скопировано' : 'С таймкодом'}
                        </button>
                      </>
                    )}
                    {nextInfo && (
                      <button type="button" className="btn btn-primary" onClick={goNext}>
                        Следующее видео <IconChevronRight size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {video.description && (
                  <div className={`watch-desc ${longDesc && !descExpanded ? 'is-collapsed' : ''}`}>
                    <p>
                      <RichDescription text={video.description} onSeek={seekTo} duration={video.duration} />
                    </p>
                    {longDesc && (
                      <button type="button" className="watch-desc-toggle" onClick={() => setDescExpanded((x) => !x)}>
                        {descExpanded ? 'Свернуть' : 'Показать полностью'}
                      </button>
                    )}
                  </div>
                )}

                <div className="watch-hint">
                  <IconKeyboard size={16} />
                  <span>
                    Нажмите <kbd>?</kbd> для списка горячих клавиш. Пробел — пауза, <kbd>←</kbd> <kbd>→</kbd> — перемотка, <kbd>F</kbd> — полный экран.
                  </span>
                </div>
              </section>
            </div>
          )}

          {hasSidebar && !chromeless && (
            <aside className="watch-side-area">
              <PlaylistPanel playlist={playlist} currentId={videoId} onSelect={goToVideo} />
            </aside>
          )}
        </div>
      </main>
      {!isInIframe && !chromeless && <SiteFooter />}

      {copied === 'error' && <div className="snackbar">Не удалось скопировать ссылку</div>}
    </div>
  );
}

export default VideoPlayer;
